import clsx from 'clsx';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  type Dispatch,
  type Ref,
} from 'react';
import type { RequireAtLeastOne } from 'type-fest';

import {
  type DataTestId,
  type StylingProps,
} from '@dynatrace/strato-components/core';

import { CalendarHeader } from './calendar-header/CalendarHeader.js';
import {
  calendarBodyCSS,
  calendarCSS,
  calendarDisabledCSS,
} from './Calendar.sty.js';
import { CalendarContext } from './contexts/CalendarContext.js';
import { UseCalendarProps, useCalendar } from './hooks/useCalendar.js';
import { validateCalendarSelection } from './state/calendar-validation.js';
import type { CalendarState, CalendarViewType } from './types/shared-types.js';
import { isCompleteSelection } from './utils/helpers.js';
import { SelectDayView } from './views/SelectDayView.js';
import { SelectMonthView } from './views/SelectMonthView.js';
import { SelectYearView } from './views/SelectYearView.js';
import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import type { TimeframeV2 } from '../../core/types/time.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useFormFieldValidation } from '../forms-core/hooks/useFormFieldValidation.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import {
  FormFieldMessagesAction,
  type FormFieldMessagesReducerAction,
} from '../forms-core/state/formfield-messages-state-reducer.js';
import { mapToTimeframeV2 } from '../forms-core/time/timeParser.js';
import { isTimeframeV2 } from '../forms-core/time/types.js';
import type { FormControlRef, TimeRangePickerRef } from '../shared-types.js';

//#region types
/** @internal */
export type CalendarValueType<Type extends CalendarType> = Type extends 'range'
  ?
      | RequireAtLeastOne<TimeframeV2>
      | RequireAtLeastOne<{ from: string; to: string }>
      | null
  : string | null | Date;

/** @internal */
export type CalendarStrictValueType<Type extends CalendarType> =
  Type extends 'range' ? TimeframeV2 | null : string | null;

/**
 * @internal
 */
export type CalendarType = 'single' | 'range';

/** @internal */
export type CalendarProps<Type extends CalendarType> = FormControlProps<
  CalendarValueType<Type>,
  (date: CalendarStrictValueType<Type>) => void
> &
  StylingProps &
  DataTestId & {
    /** The earliest selectable date. It will always consider the start of the day as time. */
    min?: string;
    /** The latest selectable date. It will always consider the end of the day as time. */
    max?: string;
    /** Determines the calendar type. Single allows the selection of one date and range allows a range selection with a from and to value.
     * @defaultValue 'single'
     */
    type?: Type;
    /**
     * The precision of the time shown in the display value.
     * @defaultValue 'minutes'
     */
    precision?: 'minutes' | 'seconds' | 'milliseconds';
  };
//#endregion

function getControllableStateProps<T extends CalendarType>({
  defaultValue,
  value,
  onChange,
  type,
}: {
  defaultValue: CalendarProps<T>['defaultValue'];
  value: CalendarProps<T>['value'];
  onChange: CalendarProps<T>['onChange'];
  type: CalendarType;
}) {
  if (isTimeframeV2(defaultValue, type) && isTimeframeV2(value, type)) {
    const mappedDefaultValue = defaultValue
      ? mapToTimeframeV2(defaultValue)
      : defaultValue;
    const mappedValue = value ? mapToTimeframeV2(value) : value;

    return { defaultValue: mappedDefaultValue, value: mappedValue, onChange };
  }

  return {
    defaultValue:
      defaultValue instanceof Date
        ? (defaultValue.toISOString() as CalendarStrictValueType<T>)
        : defaultValue,
    value:
      value instanceof Date
        ? (value.toISOString() as CalendarStrictValueType<T>)
        : value,
    onChange,
  };
}

/** Render the correct view depending on the currentView state. */
function renderView(view: CalendarViewType) {
  switch (view) {
    case 'month':
      return <SelectMonthView />;
    case 'year':
      return <SelectYearView />;
    default:
      return <SelectDayView />;
  }
}

function validate(
  state: CalendarState,
  dispatch?: Dispatch<FormFieldMessagesReducerAction>,
) {
  validateCalendarSelection(
    state.selectedDate,
    state,
    (args: FormFieldMessagesReducerAction) => {
      // Delay the ErrorMessage update by a tick to not update the calendar state and the message state at the same time.
      window.setTimeout(() => {
        dispatch?.(args);
      });
    },
    true,
  );
}

/**
 * Calendar component
 * Renders the calendar header and body depending on the current view
 * @internal
 */
function MappedCalendar<Type extends CalendarType = 'single'>(
  props: CalendarProps<Type>,
  forwardedRef: Ref<
    | FormControlRef<HTMLDivElement>
    | (Omit<FormControlRef<HTMLDivElement>, 'inputRef'> &
        Pick<TimeRangePickerRef<HTMLDivElement>, 'inputRef'>)
  >,
) {
  const {
    value,
    onChange,
    defaultValue,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    id: propId,
    disabled: propDisabled,
    'aria-disabled': ariaDisabledProp,
    required: propRequired,
    name,
    readOnly = false,
    // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
    // @ts-ignore Strict mode transition - needs to be investigated
    type = 'single',
    precision,
    ...remainingProps
  }: CalendarProps<Type> = props;

  const [internalValue, setInternalValue] = useControllableState(
    // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
    // @ts-ignore Strict mode transition - needs to be investigated
    getControllableStateProps<typeof type>({
      defaultValue,
      value,
      onChange,
      type,
    }),
  );

  // Imperative Handle Construction
  const calendarRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLInputElement>(null);
  const refTo = useRef<HTMLInputElement>(null);

  const { uniqueId, state, dispatch } = useCalendar<Type>({
    calendarRef,
    value: internalValue,
    onChange: setInternalValue,
    type,
    precision,
    ...remainingProps,
  } as UseCalendarProps<Type>);

  const { state: messageState, dispatch: formMessageDispatch } =
    useFormFieldMessagesContext();

  useImperativeHandle(
    forwardedRef,
    ():
      | FormControlRef<HTMLDivElement>
      | (Omit<FormControlRef<HTMLDivElement>, 'inputRef'> &
          Pick<TimeRangePickerRef<HTMLDivElement>, 'inputRef'>) => {
      if (type === 'single') {
        return {
          element: calendarRef.current,
          inputRef: ref.current,
          validate() {
            validate(state, formMessageDispatch);
            formMessageDispatch?.({
              type: FormFieldMessagesAction.SETISTOUCHED,
              payload: { isTouched: true },
            });
          },
        };
      }

      return {
        element: calendarRef.current,
        inputRef: {
          from: ref?.current,
          to: refTo?.current,
        },
        validate() {
          validate(state, formMessageDispatch);
        },
      };
    },
    [formMessageDispatch, state, type],
  );

  const {
    disabled: contextDisabled,
    required: contextRequired,
    id: controlId,
    ariaDisabled,
  } = useSyncedFormControlProps({
    propDisabled,
    propRequired,
    propId,
    propAriaDisabled: ariaDisabledProp,
  });

  const {
    ariaProps,
    onChange: validationOnChange,
    onBlur,
    onInvalid,
  } = useFormFieldValidation(ref);
  const isValueMissing = internalValue === undefined || internalValue === null;

  // cleans up the required error when the value changed
  useEffect(() => {
    if (messageState.showError) {
      validationOnChange();
    }

    // eventListeners is excluded since it is changed every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValueMissing, messageState.showError]);

  const labelRef = useRef<string>(`calendar-body-label-${uniqueId}`);

  const disabled = propDisabled || contextDisabled;
  const required = propRequired || contextRequired;
  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  const getNativeProps = (suffix = '') => ({
    readOnly: readOnly,
    required,
    disabled,
    'aria-disabled': ariaDisabled,
    name: name && `${name}${suffix}`,
    id: controlId,
  });

  const calenderContextValue = useMemo(
    () => ({ state, dispatch, readOnly }),
    [state, dispatch, readOnly],
  );

  return (
    <CalendarContext.Provider value={calenderContextValue}>
      <div
        className={clsx(calendarCSS({ readOnly }), consumerClassName, {
          [calendarDisabledCSS]: disabledOrAriaDisabled,
        })}
        data-testid={dataTestId}
        ref={calendarRef}
        style={consumerStyle}
        {...remainingProps}
      >
        <CalendarHeader view={state.currentView} labelId={labelRef.current} />
        <div aria-labelledby={labelRef.current}>
          <table
            role="grid"
            className={calendarBodyCSS({
              isReadOnly: !!readOnly,
            })}
          >
            {renderView(state.currentView)}
          </table>
        </div>
      </div>
      <VisuallyHidden>
        {isTimeframeV2(state.selectedDate, state.type) ? (
          <>
            <input
              onChange={validationOnChange}
              onBlur={onBlur}
              onInvalid={onInvalid}
              {...ariaProps}
              {...getNativeProps('-from')}
              ref={ref}
              // Only set if a complete timeframe is available to rely on the browser validation for required.
              value={
                isCompleteSelection(type, state.selectedDate)
                  ? state.selectedDate?.from?.value
                  : ''
              }
              tabIndex={-1}
            />
            {/* This input is ONLY used for form submission and not for validation. */}
            <input
              {...getNativeProps('-to')}
              ref={refTo}
              value={state.selectedDate?.to?.value ?? ''}
              id={`${controlId}-to`}
              type="hidden"
            />
          </>
        ) : (
          <input
            onChange={validationOnChange}
            onBlur={onBlur}
            onInvalid={onInvalid}
            {...ariaProps}
            {...getNativeProps()}
            ref={ref}
            value={state.selectedDate?.value ?? ''}
            tabIndex={-1}
          />
        )}
      </VisuallyHidden>
    </CalendarContext.Provider>
  );
}

MappedCalendar['displayName'] = 'Calendar';

/**
 * Calendar Component
 * @internal
 */
export const Calendar = forwardRef(MappedCalendar);
